import { useEffect, useState } from 'react';
import './App.css';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';
import Matchmaking from './pages/Matchmaking/Matchmaking';
import Login from './pages/Login/Login'
import Control from './pages/Control/Control';
import { ThemeProvider } from '@mui/material'
import { Navbar } from './components/Navbar';
import { CssBaseline } from "@mui/material";
import { getCookie, logIntoHost, CheckLoginStatus, checkAnnouncements, Announcement } from './ts/Common';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { useCookies } from 'react-cookie';
import SpectreTheme from './SpectreTheme';
import { Machine, LocalHostConnection } from './ts/Connections';
import { refreshMatchmakingData, recreateConnectionFromType } from './ts/matchmakingRefresh';
import { MachineEvent } from './ts/MachineEvent';
import AuthRedirect from './pages/AuthRedirect/AuthRedirect';
import AuthRedirectLogOut from './pages/AuthRedirect/AuthRedirectLogOut';
import LoginSSO from './pages/AuthRedirect/LoginSSO';
import LogoutSSO from './pages/AuthRedirect/LogoutSSO';
import { HttpResponse } from 'msw';
import { useMsal } from '@azure/msal-react';
import { AlertProvider } from './AlertContext';
import { FeatureFlagsProvider } from './FeatureFlagsContext';
import MachinePingWrapper from './MachinePingWrapper';
import { useLanguageDetector } from './i18n/useLanguageDetector';
import { ONLINE_MACHINES_KEY } from './ts/matchmakingRefresh';
import { LOCAL_MACHINES_KEY } from './ts/matchmakingRefresh';

const MY_HOST = import.meta.env.VITE_MY_HOST || "https://spectre-licensing.com"
let didInit = false;

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [selectedMachine, setSelectedMachine] = useState<any>(false);
  const [signInProgress, setSignInProgress] = useState<boolean>(false);
  const [cookies, , removeCookie] = useCookies(['user64']);
  const [cookieSpinner, setCookieSpinner] = useState<boolean>(false);
  const [user, setUser] = useState<string>("");
  const [showMachineSettings, setShowMachineSettings] = useState<boolean>(false);
  const [showCalibration, setShowCalibration] = useState<boolean>(false);
  const [showSupport, setShowSupport] = useState<boolean>(false);
  const [showCertification, setShowCertification] = useState<boolean>(false);
  const [onlineMachines, setOnlineMachines] = useState<Array<Machine>>([]);
  const [machineEvents, setMachineEvents] = useState<MachineEvent[]>([]);
  const [localMachines, setLocalMachines] = useState<Array<Machine>>([]);
  const [isRefreshingMatchmaking, setIsRefreshingMatchmaking] = useState<boolean>(false);
  const [showLoginError, setShowLoginError] = useState<string>("none")
  const [loginErrorText, setLoginErrorText] = useState<string>("");
  const [mobileNavbarOpen, setMobileNavbarOpen] = useState<boolean>(false);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const activeAccount = accounts.find((account) => account.homeAccountId === accounts[0].homeAccountId);

  // Central refresh for matchmaking lists (used by Navbar and others)
  const refreshMatchmaking = async (): Promise<void> => {
    if (isRefreshingMatchmaking) return; // avoid concurrent refreshes
    setIsRefreshingMatchmaking(true);
    try {
      if (!cookies.user64) return; // need auth cookie for proxy connections
      const { onlineMachines: nextOnline, localMachines: nextLocal } = await refreshMatchmakingData(cookies.user64);
      setLocalMachines(nextLocal);
      setOnlineMachines(nextOnline);
    } catch (err) {
      console.error('Failed to refresh matchmaking:', err);
    } finally {
      setIsRefreshingMatchmaking(false);
    }
  };

  // Initialize language detector
  useLanguageDetector();

  useEffect(() => {
    if (location.pathname === '/authRedirectLogOut' || location.pathname === '/authRedirect') {
      return;
    }
    if (!didInit) {
      didInit = true;
      
      // Check if we have a user64 cookie first
      const credentials = getCookie("user64");
      
      if (credentials) {
        // If we have a cookie, use it for authentication (for proxy machines)
        setSignInProgress(true);
        setCookieSpinner(true);
        logIntoHost(credentials, `${MY_HOST}/api/Account/LoginPWA`)
          .then((response: any) => {
            if (response.ok) {
              setIsLoggedIn(true);
              setSignInProgress(false);
              setCookieSpinner(false);
              setUser(atob(credentials).split(":")[0]);
            } else {
              setIsLoggedIn(false);
              setShowLoginError('block');
              setLoginErrorText('Please sign in again');
              setSignInProgress(false);
              setCookieSpinner(false);
              navigate('/login');
            }
          })
          .catch((err: any) => {
            console.log(err);
            setShowLoginError("block");
            setSignInProgress(false);
            setLoginErrorText("Error logging in, please try again");
            setCookieSpinner(false);
          });
      } else {
        // No cookie, check login status (for SSO users)
        CheckLoginStatus().then((response: HttpResponse) => {
          if (response.ok) {
            setIsLoggedIn(true);
            return response.json();
          } else {
            console.log('Check Login Status: Not logged in');
            navigate('/login');
            return;
          }
        })
          .then((responseJson: { email: string, ssoEnabled: boolean }) => {
            if (!responseJson) {
              return;
            }
            if (!responseJson.ssoEnabled && !cookies.user64) {
              console.log('Check Login Status: Not logged in and no cookie - force re-login');
              setIsLoggedIn(false);
              navigate('/login');
              return;
            }
            if (responseJson && responseJson.ssoEnabled) {
              setUser(responseJson.email);
              if (!activeAccount) {
                navigate('/login');
              }
            } else {
              setUser(responseJson.email);
            }
          })
          .catch((error) => {
            console.error(error);
            setShowLoginError("block");
            setSignInProgress(false);
            setLoginErrorText("Error logging in, please try again");
            setCookieSpinner(false);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (!isLoggedIn || (onlineMachines.length > 0 || localMachines.length > 0)) return;
    const cachedOnlineMachines = localStorage.getItem(ONLINE_MACHINES_KEY);
    const cachedLocalMachines = localStorage.getItem(LOCAL_MACHINES_KEY);
    if (cachedOnlineMachines || cachedLocalMachines) {
      const parsedOnlineMachines = JSON.parse(cachedOnlineMachines || '[]');
      const parsedLocalMachines = JSON.parse(cachedLocalMachines || '[]');

      if (parsedLocalMachines.length) {
        parsedOnlineMachines.push(parsedLocalMachines[0]);
      }

      const machineArray: Machine[] = [];
      if (parsedOnlineMachines.length > 0) {
        for (const machine of parsedOnlineMachines) {
          try {
            const connection = recreateConnectionFromType(machine.connectionType, machine, cookies.user64);
            machineArray.push(new Machine(
              connection,
              machine.machineName,
              machine.localIP,
              machine.publicIP,
              machine.matchmakingID,
              machine.location,
              machine.macAddress,
              machine.scanMode,
              machine.certification,
              machine.version,
              machine.serialNumber,
              machine.isOutdated
            ));
          } catch (error) {
            console.error('Failed to recreate machine from cache:', error, machine);
          }
        }
        const localMachines = machineArray.filter((m: Machine) => m.connection instanceof LocalHostConnection);
        setOnlineMachines(machineArray);
        setLocalMachines(localMachines);
      }
    }
  }, [isLoggedIn, localMachines.length, location.pathname, onlineMachines.length]);

  useEffect(() => {
    selectedMachine ? document.title = `Spectre Link - ${selectedMachine.machineName}` : document.title = 'Spectre Link'
  }, [selectedMachine]);

useEffect(() => {
  let cancelled = false;

  checkAnnouncements()
    .then((data: Announcement[]) => {
      if (cancelled) return;
      setAnnouncements((prev) => {
        const hasChanged = JSON.stringify(data) !== JSON.stringify(prev);
        return hasChanged ? data : prev;
      });
    })
    .catch(() => {});

  return () => {
    cancelled = true;
  };
}, [location.pathname]);

  if (!isLoggedIn) {
    return (
      <div>
        <ThemeProvider theme={SpectreTheme}>
          <AlertProvider>
            <AnnouncementBanner
              announcements={announcements}
              offsetTop={0}
            />
            <Routes>
              <Route path="/" element={<Login setIsLoggedIn={setIsLoggedIn} signInProgress={signInProgress} setSignInProgress={setSignInProgress} cookieSpinner={cookieSpinner} setUser={setUser} removeCookie={removeCookie} showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} signInProgress={signInProgress} setSignInProgress={setSignInProgress} cookieSpinner={cookieSpinner} setUser={setUser} removeCookie={removeCookie} showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/authRedirect" element={<AuthRedirect setIsLoggedIn={setIsLoggedIn} setSignInProgress={setSignInProgress} setUser={setUser} removeCookie={removeCookie} setShowLoginError={setShowLoginError} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/authRedirectLogOut" element={<AuthRedirectLogOut setIsLoggedIn={setIsLoggedIn} setUser={setUser} removeCookie={removeCookie} setShowLoginError={setShowLoginError} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/LoginSSO" element={<LoginSSO />} />
              <Route path="/LogoutSSO" element={<LogoutSSO />} />
              <Route path="login" element={isLoggedIn ? <Navigate to="/matchmaking" /> : <Login setIsLoggedIn={setIsLoggedIn} signInProgress={signInProgress} setSignInProgress={setSignInProgress} cookieSpinner={cookieSpinner} setUser={setUser} removeCookie={removeCookie} showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/" element={isLoggedIn ? <Navigate to="/matchmaking" /> : <Login setIsLoggedIn={setIsLoggedIn} signInProgress={signInProgress} setSignInProgress={setSignInProgress} cookieSpinner={cookieSpinner} setUser={setUser} removeCookie={removeCookie} showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
            </Routes>
          </AlertProvider>
        </ThemeProvider>
      </div>)
  }

  else {
    return (
      <ThemeProvider theme={SpectreTheme}>
        <AlertProvider>
          <CssBaseline />
          <Navbar
            setIsLoggedIn={setIsLoggedIn}
            user={user}
            removeCookie={removeCookie}
            setShowMachineSettings={setShowMachineSettings}
            setShowCalibration={setShowCalibration}
            setShowSupport={setShowSupport}
            setShowCertification={setShowCertification}
            selectedMachine={selectedMachine}
            setSelectedMachine={setSelectedMachine}
            onlineMachines={onlineMachines}
            setMachineEvents={setMachineEvents}
            mobileNavbarOpen={mobileNavbarOpen}
            setMobileNavbarOpen={setMobileNavbarOpen}
            refreshMatchmaking={refreshMatchmaking}
            isRefreshingMatchmaking={isRefreshingMatchmaking}
            setSettingsLoaded={setSettingsLoaded}
          />
          <AnnouncementBanner
            announcements={announcements}
            offsetTop={64}
          />
          <div className="App">
            <Routes>
              <Route path="login" element={isLoggedIn ? <Navigate to="/matchmaking" /> : <Login showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/LoginSSO" element={<LoginSSO />} />
              <Route path="/" element={isLoggedIn ? <Navigate to="/matchmaking" /> : <Login showLoginError={showLoginError} setShowLoginError={setShowLoginError} loginErrorText={loginErrorText} setLoginErrorText={setLoginErrorText} />} />
              <Route path='/matchmaking/*' element={< Matchmaking setIsLoggedIn={setIsLoggedIn} setSelectedMachine={setSelectedMachine} cookies={cookies} removeCookie={removeCookie} onlineMachines={onlineMachines} setOnlineMachines={setOnlineMachines} localMachines={localMachines} setLocalMachines={setLocalMachines}
                setMachineEvents={setMachineEvents} user={user}></Matchmaking>}></Route>
              <Route
                path='/matchmaking/control/:id'
                element={
                  <MachinePingWrapper
                    machine={selectedMachine}
                    setSelectedMachine={setSelectedMachine}
                    cookies={{ ...cookies, user64: cookies.user64 ?? "" }}

                  >
                    <FeatureFlagsProvider machine={selectedMachine}>
                      <Control
                        machine={selectedMachine}
                        showMachineSettings={showMachineSettings}
                        setShowMachineSettings={setShowMachineSettings}
                        showCalibration={showCalibration}
                        setShowCalibration={setShowCalibration}
                        showSupport={showSupport}
                        setShowSupport={setShowSupport}
                        showCertification={showCertification}
                        setShowCertification={setShowCertification}
                        onlineMachines={onlineMachines}
                        setSelectedMachine={setSelectedMachine}
                        machineEvents={machineEvents}
                        setMachineEvents={setMachineEvents}
                        user={user}
                        mobileNavbarOpen={mobileNavbarOpen}
                        settingsLoaded={settingsLoaded}
                        setSettingsLoaded={setSettingsLoaded}
                      />
                    </FeatureFlagsProvider>
                  </MachinePingWrapper>
                }
              />
              <Route path="/authRedirect" element={<AuthRedirect setIsLoggedIn={setIsLoggedIn} setSignInProgress={setSignInProgress} setUser={setUser} removeCookie={removeCookie} setShowLoginError={setShowLoginError} setLoginErrorText={setLoginErrorText} />} />
              <Route path="/authRedirectLogOut" element={<AuthRedirectLogOut setIsLoggedIn={setIsLoggedIn} setUser={setUser} removeCookie={removeCookie} setShowLoginError={setShowLoginError} setLoginErrorText={setLoginErrorText} />} />
            </Routes>
          </div>
        </AlertProvider>
      </ThemeProvider>
    );
  }
}

export default App;